// Per-franchise Single Franchise Audit entries.
//
// A KAM whose config picks franchises explicitly gets one named entry per
// (franchise, market) pair — "KFC Audit" — on every browse surface, replacing
// the generic entry. The server decides who is eligible and what the pairs are
// (`dashoneNavigation`); this module turns its pairs into browse entries and
// lays them out.

import type { WorkflowAgent } from "@dashai/components/dashai/WorkflowAgentsGrid";

export const SFA_BASE_ID = "single-franchise-audit";

// Up to this many pairs render as flat entries; above it the sidebar shows a
// single expandable group and the home grid collapses into a "+N more" card.
export const SFA_VARIANT_FLAT_CAP = 4;

export interface SfaVariant {
  // Unique per (franchise, market) pair, stable across renders.
  key: string;
  // The chain value as stored: a franchise_name string (personaConfigPrefill
  // feeds the same value straight into the card's franchise prefill).
  franchise: string;
  // Country code — the config.markets key the franchise came from.
  country: string;
  // "<Franchise> Audit", suffixed with the market code only when the same
  // franchise name is configured in more than one market.
  title: string;
}

// What a browse surface renders, as a tagged union rather than a WorkflowAgent
// subtype.
//
// A variant is NOT substitutable for a catalogue agent: its id is synthesised
// per (franchise, market) pair and resolves to nothing in `workflowAgents`, so
// any caller that hands that id to `workflowAgents.find()` gets undefined and
// silently does nothing. Modelling variants as `extends WorkflowAgent` made
// that a runtime hazard every call site had to remember to guard. The tag makes
// it a compile error instead: `kind` has to be narrowed before `agent` is
// reachable, and `base` carries the id that does resolve.
export type BrowseEntry =
  | { kind: "catalogue"; agent: WorkflowAgent }
  | {
      kind: "variant";
      // The synthesised entry: display fields to render, and an id unique per
      // pair so React keys and the ">cap" grouping stay stable.
      agent: WorkflowAgent;
      // The catalogue id to launch, and the inputs to launch it with.
      base: typeof SFA_BASE_ID;
      prefill: { franchise: string; country: string };
    };

export const catalogueEntry = (agent: WorkflowAgent): BrowseEntry => ({
  kind: "catalogue",
  agent,
});

// Whether an eligible KAM's generic SFA row is replaced by per-franchise rows.
// The home grid and the sidebar get that from the entry list; the command
// palette builds its own row shape (SearchResult shares no fields with
// WorkflowAgent) and so needs the rule itself. One owner keeps the two from
// disagreeing about which row disappears.
export const isReplacedGenericSfa = (workflowId: string, sfaEligible: boolean): boolean =>
  sfaEligible && workflowId === SFA_BASE_ID;

// How many per-franchise entries a browse surface renders before the "+N more"
// expander takes over. Lives here beside SFA_VARIANT_FLAT_CAP rather than in
// the grid that calls it: this is variant logic, and pure, so the boundary
// cases are testable without rendering anything.
export function splitSfaVariants(
  entries: BrowseEntry[],
  expanded: boolean
): {
  visibleIds: ReadonlySet<string>;
  hidden: Extract<BrowseEntry, { kind: "variant" }>[];
  expanderAfterId: string | undefined;
} {
  const variants = entries.filter(
    (e): e is Extract<BrowseEntry, { kind: "variant" }> => e.kind === "variant"
  );
  const collapse = variants.length > SFA_VARIANT_FLAT_CAP && !expanded;
  const shown = collapse ? variants.slice(0, 3) : variants;
  return {
    visibleIds: new Set(shown.map((e) => e.agent.id)),
    hidden: collapse ? variants.slice(3) : [],
    // The expander sits after the last shown variant. Variants are contiguous
    // (spliced into the generic entry's slot by usePersonaVisibility), so one cut
    // point is enough.
    expanderAfterId: collapse ? variants[2]?.agent.id : undefined,
  };
}

// How a list surface folds per-franchise entries: past the flat cap they
// collapse into one "Single Franchise Audit (N)" group that takes their slot.
// Returns null at or under the cap, where the list renders flat. Variants are
// contiguous, so the entries before and after the first one frame the group.
export function groupSfaVariants(entries: BrowseEntry[]): {
  before: BrowseEntry[];
  variants: Extract<BrowseEntry, { kind: "variant" }>[];
  after: BrowseEntry[];
} | null {
  const variants = entries.filter(
    (e): e is Extract<BrowseEntry, { kind: "variant" }> => e.kind === "variant"
  );
  if (variants.length <= SFA_VARIANT_FLAT_CAP) return null;
  const firstVariantIdx = entries.findIndex((e) => e.kind === "variant");
  return {
    before: entries.slice(0, firstVariantIdx),
    variants,
    after: entries.slice(firstVariantIdx).filter((e) => e.kind !== "variant"),
  };
}

// Materialize the variants as browse entries, cloned from the catalogue's
// generic SFA entry so outputType/outputIcon/hasInputDialog (and the detail
// content, rendered under the BASE id) stay in lockstep with it.
export function makeVariantEntries(base: WorkflowAgent, variants: SfaVariant[]): BrowseEntry[] {
  return variants.map((variant) => ({
    kind: "variant" as const,
    agent: {
      ...base,
      id: variant.key,
      name: variant.title,
      description: `Full execution audit of ${variant.franchise} across delivery platforms.`,
    },
    base: SFA_BASE_ID,
    prefill: { franchise: variant.franchise, country: variant.country },
  }));
}
